import Sentry, { featureTags } from '@/services/sentry'
import Vue from 'vue'
import slideTypeCsatApi from '@/api/slideTypeCsat'
import slideTypeUsageApi from '@/api/slideTypeUsage'
import Util from '@/util'

// BAY-4: per-slide-type CSAT ratings for the scalable slide-type picker's
// Rating cell. Fetched at most ONCE per page load: every picker / details-
// modal mount dispatches `fetchSlideTypesCsat`, but only the first call hits
// the network — later calls share the in-flight promise or return
// immediately once it settled. A failed request is not retried: the rating
// getter returns null and `areRatingsLoaded` stays false, so callers keep the
// Rating cell hidden and a broken endpoint is never hammered on each open.

const SET_RATINGS = 'SET_RATINGS'
const SET_GRADUATED = 'SET_GRADUATED'
const SET_SETTLED = 'SET_SETTLED'
const SET_FETCH_PROMISE = 'SET_FETCH_PROMISE'
const SET_RATE_STRIP_OPEN = 'SET_RATE_STRIP_OPEN'
const SET_HOSTED_SESSIONS = 'SET_HOSTED_SESSIONS'
const SET_USAGE_BANNER_DISMISSED = 'SET_USAGE_BANNER_DISMISSED'
const CLEAR_USAGE_BANNER_DISMISSED = 'CLEAR_USAGE_BANNER_DISMISSED'

export const USAGE_BANNER_MIN_HOSTED_SESSIONS = 1
const USAGE_BANNER_DISMISSED_STORAGE_PREFIX = 'slide-type-usage-banner-dismissed-'

// A dismissal written by a rating is undone when that rating is cleared; a closed banner stays closed.
const DISMISSED_BY_CLOSE = '1'
const DISMISSED_BY_RATING = 'rated'

const readDismissal = (rateKey) => {
  try {
    return localStorage.getItem(`${USAGE_BANNER_DISMISSED_STORAGE_PREFIX}${rateKey}`)
  } catch (error) {
    return null
  }
}

const writeDismissal = (rateKey, reason) => {
  try {
    if (reason) localStorage.setItem(`${USAGE_BANNER_DISMISSED_STORAGE_PREFIX}${rateKey}`, reason)
    else localStorage.removeItem(`${USAGE_BANNER_DISMISSED_STORAGE_PREFIX}${rateKey}`)
  } catch (error) {
    // storage unavailable: the banner then returns on the next page load
  }
}

const toRating = (entry) => {
  if (!entry) return null
  const stars = Number(entry.stars)
  const ratingCount = Number(entry.ratingCount)
  if (!Number.isFinite(stars) || !Number.isFinite(ratingCount) || ratingCount <= 0) return null
  return {
    stars: Math.min(Math.max(stars, 0), 5),
    csatPercent: Number(entry.csatPercent) || 0,
    ratingCount,
  }
}

const _state = {
  // { [slideType]: { stars, csatPercent, ratingCount } }
  ratings: null,
  graduated: {},
  settled: false,
  // Set on the first dispatch and never cleared — this is the "only once" guard.
  fetchPromise: null,
  isRateStripOpen: false,
  hostedSessions: {},
  dismissedUsageBanners: {},
}

const loadHostedSessionCount = async (commit, slideTypeKey) => {
  try {
    commit(SET_HOSTED_SESSIONS, { slideTypeKey, count: await slideTypeUsageApi.getHostedSessionCount(slideTypeKey) })
  } catch (error) {
    Sentry.captureException(error, {
      tags: featureTags({ featureFlag: 'SLPV-220-slide-type-csat-revamp', featureName: 'slide-type-csat', taskId: 'SLPV-220' }),
    })
  }
}

const _getters = {
  isRateStripOpen: (state) => state.isRateStripOpen,
  // Hosting is counted per stored slide type; the dismissal is per rated variant.
  shouldShowUsageBanner: (state) => (slideType, rateKey) => {
    if (!slideType || !rateKey) return false
    if (state.dismissedUsageBanners[rateKey]) return false
    return (state.hostedSessions[slideType] || 0) >= USAGE_BANNER_MIN_HOSTED_SESSIONS
  },
  hostedSessionCount: (state) => (slideTypeKey) => state.hostedSessions[slideTypeKey] || 0,
  areRatingsLoaded: (state) => state.ratings !== null,
  // Lookup a rating by (type, slideType). Native quiz variants all share
  // `type='pickAnswer'` and are discriminated by `slideType`, so a lookup
  // on `type` alone returns the same rating for every variant. Normalize
  // the Pick Answer family through `Util.getCorrectSlideTypeName` and key
  // on `pickAnswer::${variant}`; fall back to the raw type for every
  // other entry (marketplace / native base types keyed by `type` only).
  isCsatSettled: (state) => state.settled,
  isSlideTypeGraduated: (state) => (type, slideType) => {
    if (!type) return false
    const key = type === 'pickAnswer'
      ? `pickAnswer::${Util.getCorrectSlideTypeName(type, slideType || null)}`
      : type
    return state.graduated[key] === true
  },
  slideTypeRating: (state) => (type, slideType) => {
    if (!type || !state.ratings) return null
    if (type === 'pickAnswer') {
      const variant = Util.getCorrectSlideTypeName(type, slideType || null)
      return state.ratings[`pickAnswer::${variant}`] || null
    }
    return state.ratings[type] || null
  },
}

const mutations = {
  [SET_SETTLED](state) {
    Vue.set(state, 'settled', true)
  },
  [SET_GRADUATED](state, graduated) {
    Vue.set(state, 'graduated', graduated)
  },
  [SET_RATINGS](state, ratings) {
    Vue.set(state, 'ratings', ratings)
  },
  [SET_FETCH_PROMISE](state, promise) {
    Vue.set(state, 'fetchPromise', promise)
  },
}

const extraMutations = {
  [SET_RATE_STRIP_OPEN](state, isOpen) {
    Vue.set(state, 'isRateStripOpen', isOpen)
  },
  [SET_HOSTED_SESSIONS](state, { slideTypeKey, count }) {
    Vue.set(state.hostedSessions, slideTypeKey, count)
  },
  [SET_USAGE_BANNER_DISMISSED](state, rateKey) {
    Vue.set(state.dismissedUsageBanners, rateKey, true)
  },
  [CLEAR_USAGE_BANNER_DISMISSED](state, rateKey) {
    Vue.delete(state.dismissedUsageBanners, rateKey)
  },
}

const actions = {
  toggleRateStrip({ commit, state }) {
    commit(SET_RATE_STRIP_OPEN, !state.isRateStripOpen)
  },
  async fetchHostedSessions({ commit, state, rootGetters }, { slideType, rateKey } = {}) {
    if (!slideType || !rootGetters['featureToggles/canUseSlideTypeCsatRevamp']) return
    if (rateKey && readDismissal(rateKey)) commit(SET_USAGE_BANNER_DISMISSED, rateKey)
    if (state.hostedSessions[slideType] !== undefined) return
    await loadHostedSessionCount(commit, slideType)
  },
  // The editor stays mounted while presenting, so counts read before hosting must be re-read.
  refreshHostedSessions({ commit, state }) {
    const cachedKeys = Object.keys(state.hostedSessions)
    return Promise.all(cachedKeys.map((slideTypeKey) => loadHostedSessionCount(commit, slideTypeKey)))
  },
  // Stored only, not committed, so a banner being rated stays open for the feedback step.
  recordUsageRating({ commit, rootGetters }, { rateKey, rating } = {}) {
    if (!rateKey || !rootGetters['featureToggles/canUseSlideTypeCsatRevamp']) return
    const dismissal = readDismissal(rateKey)
    if (rating && !dismissal) writeDismissal(rateKey, DISMISSED_BY_RATING)
    else if (!rating && dismissal === DISMISSED_BY_RATING) {
      writeDismissal(rateKey, null)
      commit(CLEAR_USAGE_BANNER_DISMISSED, rateKey)
    }
  },
  dismissUsageBanner({ commit }, rateKey) {
    if (!rateKey) return
    writeDismissal(rateKey, DISMISSED_BY_CLOSE)
    commit(SET_USAGE_BANNER_DISMISSED, rateKey)
  },
  fetchSlideTypesCsat({ commit, state, rootGetters }) {
    const isEnabled = rootGetters['featureToggles/canUseScalableSlideTypePicker'] ||
      rootGetters['featureToggles/canUseSlideTypePickerSort'] ||
      rootGetters['featureToggles/canUseSlideTypeTrustBadges']
    if (!isEnabled) return Promise.resolve()
    if (state.fetchPromise) return state.fetchPromise

    const promise = slideTypeCsatApi.getSlideTypesCsat()
      .then((body) => {
        const raw = (body && body.data) || {}
        const ratings = Object.keys(raw).reduce((acc, type) => {
          const rating = toRating(raw[type])
          if (rating) acc[type] = rating
          return acc
        }, {})
        commit(SET_GRADUATED, Object.keys(raw).reduce((acc, type) => {
          if (raw[type] && raw[type].graduated === true) acc[type] = true
          return acc
        }, {}))
        commit(SET_RATINGS, ratings)
        commit(SET_SETTLED)
        return ratings
      })
      .catch((error) => {
        commit(SET_SETTLED)
        Sentry.captureException(error, {
          tags: featureTags({ featureFlag: 'none', featureName: 'slide-type-csat', taskId: 'SLPV-220' }),
        })
      })
    commit(SET_FETCH_PROMISE, promise)
    return promise
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations: { ...mutations, ...extraMutations },
}
